import { useNavigate } from 'react-router-dom'
import { OtherText } from 'styling/Typography'
import { IOpsTumSegment, OpsLossBucket } from 'types/opsAnalytics.interface'
import { IDayStripProps, IDayStripSegMeta, IDayStripTick } from 'types/opsTimelines.interface'
import { LOSS_BUCKET_META } from 'pages/Operations/Shared/lossClassifier'
import {
  BoundaryLabel,
  BoundaryLabelRow,
  LegendItem,
  LegendRow,
  LegendSwatch,
  NowBoundaryLabel,
  NowMarkerLine,
  SegGlyph,
  SegLabel,
  ShiftBoundaryLine,
  StripHead,
  StripRuler,
  StripSeg,
  StripTick,
  StripTrack,
  StripWrap,
} from './Timelines.styled'

const DATA_START_HOUR = 6

const SHIFT_CHANGE_HOUR = 18

const rulerTicks = (startHour: number, windowHours: number, stepHours: number): IDayStripTick[] => {
  const count = Math.floor(windowHours / stepHours) + 1
  return Array.from({ length: count }, (_, i) => ({
    pct: ((i * stepHours) / windowHours) * 100,
    label: `${String((startHour + i * stepHours) % 24).padStart(2, '0')}:00`,
  }))
}

const ACTIVITY_GLYPH: { [k: string]: string } = {
  load: 'L',
  haul: 'H',
  dump: 'D',
  'return': 'R',
}

const MACHINE_LEGEND_BUCKETS: OpsLossBucket[] = [
  'PRODUCTIVE',
  'BREAKDOWN',
  'PLANNED_MAINT',
  'NO_OPERATOR',
  'NO_TASK',
  'BLOCKED_CROSS_DEPT',
  'WEATHER_BLAST_HOLD',
]

const WORKER_LEGEND_BUCKETS: OpsLossBucket[] = [
  'PRODUCTIVE',
  'NO_TASK',
  'BLOCKED_CROSS_DEPT',
  'WEATHER_BLAST_HOLD',
]

const segMeta = (seg: IOpsTumSegment, variant: 'machine' | 'worker'): IDayStripSegMeta => {
  const noteText = seg.note?.toLowerCase() ?? ''
  const isBreak =
    variant === 'worker' &&
    seg.bucket === 'NO_TASK' &&
    (noteText.includes('break') || noteText.includes('pause'))
  if (isBreak) return { label: seg.note ?? 'Break', tone: 'neutral' }
  if (seg.bucket === 'NO_TASK') return { label: 'No task assigned', tone: LOSS_BUCKET_META['NO_TASK'].tone }
  return { label: seg.taskName ?? LOSS_BUCKET_META[seg.bucket].label, tone: LOSS_BUCKET_META[seg.bucket].tone }
}

const segTitle = (label: string, seg: IOpsTumSegment): string => {
  const base = `${label} · ${seg.location ?? '—'} · ${seg.toMin - seg.fromMin}min`
  return seg.note ? `${base} · ${seg.note}` : base
}

export const DayStrip = ({
  segments,
  dateLabel,
  variant = 'machine',
  startHour = 6,
  windowHours = 12,
  nowPct = null,
  nowLabel,
}: IDayStripProps) => {
  const navigate = useNavigate()
  const windowMin = windowHours * 60
  const offsetMin = (DATA_START_HOUR - startHour) * 60
  const stepHours = windowHours >= 24 ? 3 : 2
  const ticks = rulerTicks(startHour, windowHours, stepHours)
  const divisions = Math.max(ticks.length - 1, 1)
  const shiftChangePct =
    windowHours >= 24
      ? ((((SHIFT_CHANGE_HOUR - startHour + 24) % 24) * 60) / windowMin) * 100
      : null
  const showBoundaryRow = shiftChangePct != null || nowPct != null
  return (
    <StripWrap>
      <StripHead>
        <OtherText weight='600'>{dateLabel}</OtherText>
      </StripHead>
      <StripRuler $divisions={divisions}>
        {ticks.map((tick, index) => (
          <StripTick key={`${tick.label}-${index}`} $pct={tick.pct} $last={index === ticks.length - 1}>
            {tick.label}
          </StripTick>
        ))}
      </StripRuler>
      <StripTrack $divisions={divisions}>
        {shiftChangePct != null && <ShiftBoundaryLine $pct={shiftChangePct} />}
        {nowPct != null && <NowMarkerLine $pct={nowPct} />}
        {segments.map((seg) => {
          const fromMin = Math.max(seg.fromMin + offsetMin, 0)
          const toMin = Math.min(seg.toMin + offsetMin, windowMin)
          if (toMin <= fromMin) return null
          const meta = segMeta(seg, variant)
          const glyph = seg.bucket === 'PRODUCTIVE' && seg.activity ? ACTIVITY_GLYPH[seg.activity] : null
          return (
            <StripSeg
              key={seg.id}
              $tone={meta.tone}
              $left={(fromMin / windowMin) * 100}
              $width={((toMin - fromMin) / windowMin) * 100}
              $clickable={seg.taskId != null}
              title={segTitle(meta.label, seg)}
              onClick={seg.taskId != null ? () => navigate(`/operations/task/${seg.taskId}`) : undefined}
            >
              {glyph ? <SegGlyph>{glyph}</SegGlyph> : <SegLabel>{meta.label}</SegLabel>}
            </StripSeg>
          )
        })}
      </StripTrack>
      {showBoundaryRow && (
        <BoundaryLabelRow>
          {shiftChangePct != null && <BoundaryLabel $pct={shiftChangePct}>Shift change</BoundaryLabel>}
          {nowPct != null && (
            <NowBoundaryLabel $pct={nowPct}>{nowLabel ? `Now ${nowLabel}` : 'Now'}</NowBoundaryLabel>
          )}
        </BoundaryLabelRow>
      )}
    </StripWrap>
  )
}

export const BucketLegend = ({ variant = 'machine' }: { variant?: 'machine' | 'worker' }) => {
  const buckets = variant === 'worker' ? WORKER_LEGEND_BUCKETS : MACHINE_LEGEND_BUCKETS
  return (
    <LegendRow>
      {buckets.map((bucket) => (
        <LegendItem key={bucket} title={LOSS_BUCKET_META[bucket].definition}>
          <LegendSwatch $tone={LOSS_BUCKET_META[bucket].tone} />
          <OtherText grey>{LOSS_BUCKET_META[bucket].label}</OtherText>
        </LegendItem>
      ))}
      {variant === 'worker' && (
        <LegendItem title='Meal breaks and rest pauses taken during the shift.'>
          <LegendSwatch $tone='neutral' />
          <OtherText grey>Break / pause</OtherText>
        </LegendItem>
      )}
    </LegendRow>
  )
}
